import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import useChannelFilter from '@/hooks/filters/useChannelFilter'
import useMarkedFilter from '@/hooks/filters/useMarkedFilter'
import useMaterialStatusFilter from '@/hooks/filters/useMaterialStatusFilter'
import useProvinceFilter from '@/hooks/filters/useProvinceFilter'
import {
  priceListExportQueryOptions,
  priceListSearchSchema,
  usePriceList,
} from '@/hooks/queries/crt/useMaterials'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters } from '@/lib/utils/filters'
import { queryClient } from '@/main'
import { INDETERMINATE } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfigurationMenu } from './-components/ConfigurationMenu'
import { PriceListDataTable } from './-components/PriceListDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/materials/price-list/')({
  component: RouteComponent,
  validateSearch: (search) => priceListSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation('materials')
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  // The header's period: the page lists that period's price list copy, never the live SAP prices.
  const { crtPeriod } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: [],
  })

  const { isPending, error, data } = usePriceList({
    ...search,
    crtPeriod,
    page: search.page ?? 1,
    pageSize,
  })

  const { provinceFilter, province } = useProvinceFilter('code', search.province)
  const { channelFilter, channel } = useChannelFilter(search.channel)
  const { statusFilter, status } = useMaterialStatusFilter(search.status)
  const { markedFilter, marked } = useMarkedFilter(search.marked)

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const selectedFilters = {
    province: province?.length ? province : undefined,
    channel: channel?.length ? channel : undefined,
    status: status?.length ? status : undefined,
    marked: marked !== INDETERMINATE ? marked : undefined,
  }

  const filters = [provinceFilter, channelFilter, statusFilter, markedFilter]

  const showResults = () => {
    navigate({ search: { ...search, ...selectedFilters, keyword: keyword || undefined, page: 1 } })
  }

  const resetAllFilters = () => {
    setKeyword('')
    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  const getCsvExport = () =>
    queryClient.ensureQueryData(priceListExportQueryOptions({ ...search, crtPeriod }))

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('priceListPage.header')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={+(data?.rowCount ?? 0)}
            label={t('export.viewData', { ns: 'common' })}
          />
          <ConfigurationMenu crtPeriod={crtPeriod} snapshot={data?.snapshot} />
        </HStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <PriceListDataTable
            filters={filters as Filter<unknown>[]}
            search={{
              className: 'w-80',
              value: keyword,
              setValue: setKeyword,
              placeholder: t('priceListDataTable.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            data={data?.results ?? []}
            rowCount={data?.rowCount ?? 0}
            isPending={isPending}
            error={error}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
